import React from 'react';

interface PausedMessageProps {
  onResume: () => void;
  onCancel: () => void;
  onIgnore: () => void;
  currentRoute: string;
  /**
   * Ruta esperada del paso. Ausente en la pausa automática de pasos SIN
   * expectedRoute (el objetivo no está en esta pantalla): no hay a dónde
   * navegar, así que se oculta el botón "Ir a la pantalla correcta" y la
   * guía se reanuda sola cuando el objetivo aparece.
   */
  expectedRoute?: string;
}

/**
 * Overlay fijo que se muestra cuando el usuario está en una ruta distinta a la esperada.
 *
 * IMPORTANTE: Es un fixed-position DOM card (NO un <foreignObject> dentro del SVG).
 * Antes vivía dentro del <svg> cuya altura = document.scrollHeight, por lo que el
 * `y="50%"` caía en el midpoint absoluto del documento — fuera de pantalla al hacer scroll.
 * Ahora usa `fixed inset-0` para quedar centrado en el viewport sin importar el scroll.
 */
export const PausedMessage: React.FC<PausedMessageProps> = ({
  onResume,
  onCancel,
  onIgnore,
  currentRoute,
  expectedRoute
}) => {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/40 pointer-events-auto">
      <div className="bg-paper rounded-lg shadow-overlay border-2 border-warning p-6 max-w-md mx-4 text-center">
        <h3 className="text-lg font-display font-bold text-warning mb-2 flex items-center justify-center gap-2">
          <span>📍</span>
          <span>Parece que te desviaste</span>
        </h3>
        <p className="text-ink mb-4">
          {expectedRoute ? (
            <>
              Estás en <strong className="text-warning">{currentRoute}</strong> pero la guía esperaba
              <strong className="text-warning"> {expectedRoute}</strong>.
            </>
          ) : (
            <>
              No encuentro en <strong className="text-warning">{currentRoute}</strong> lo que este
              paso necesita. Navegá a la pantalla donde corresponde y la guía sigue sola.
            </>
          )}
        </p>
        <div className="flex flex-col gap-2">
          {expectedRoute && (
            <button
              onClick={onResume}
              className="w-full px-4 py-2 bg-forest text-paper rounded-lg hover:bg-forest-mid transition-colors"
            >
              Ir a la pantalla correcta
            </button>
          )}
          <button
            onClick={onIgnore}
            className="w-full px-4 py-2 bg-forest-mid text-paper rounded-lg hover:bg-forest-mid/90 transition-colors"
          >
            Saltar este paso
          </button>
          <button
            onClick={onCancel}
            className="w-full px-4 py-2 bg-paper-2 text-ink rounded-lg hover:bg-hairline/60 transition-colors"
          >
            Cancelar guía
          </button>
        </div>
      </div>
    </div>
  );
};
